/**
 * Same-origin JSON client. Credentials included. CSRF from cookie `csrf_token`.
 */

import { sipStatusLabel } from "./sipLabels";
import type {
  AdaptersStatus,
  AdminOverview,
  CallProfile,
  CallRecord,
  CallSetup,
  Campaign,
  CampaignReport,
  ConsoleSnapshot,
  HealthResponse,
  HistoryFilters,
  IvrGraph,
  IvrScript,
  Lead,
  OperatorDecisionRow,
  DtmfRangeResult,
  SecretFlags,
  SecretPatch,
  SettingsPayload,
  SetupPayload,
  SetupStatus,
  SipProfile,
  SipProfileWrite,
  StatsApiResponse,
} from "./types";
import { isCodeCollectState, isGateState } from "./digits";
import { historyEnteredCode, historyMatch, historyOtpState, historyOutcome } from "./historyOutcome";
import { EMPTY_ACTIONS, EMPTY_PARTY, EMPTY_SNAPSHOT, sipFromLight } from "./types";

const BASE = "";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly body: unknown;

  constructor(status: number, detail: string, body: unknown = null) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const token =
    readCookie("csrf_token") ||
    readCookie("csrf") ||
    readCookie("X-CSRF-Token") ||
    readCookie("csrftoken");
  if (!token) return {};
  return { "X-CSRF-Token": token };
}

function detailFromBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const record = body as Record<string, unknown>;
  const detail = record.detail ?? record.message ?? record.error;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg: unknown }).msg);
        }
        return "";
      })
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
}

/**
 * Fetch JSON from the same origin. Throws {@link ApiError} on non-2xx.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...csrfHeaders(),
  };
  const init: RequestInit = {
    method: options.method ?? (options.body || options.form ? "POST" : "GET"),
    credentials: "include",
    headers,
    signal: options.signal,
  };
  if (options.form) {
    init.body = options.form;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  const response = await fetch(`${BASE}${path}`, init);
  const parsed = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      detailFromBody(parsed, response.statusText || `HTTP ${response.status}`),
      parsed,
    );
  }
  return parsed as T;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function str(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value);
  return text || null;
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function boolish(value: unknown): boolean | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return null;
}

/**
 * Query string for `/api/history` and `/api/history.csv`.
 */
export function historySearchParams(limit: number, filters: HistoryFilters = {}): string {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  const q = filters.q?.trim();
  if (q) params.set("q", q);
  const amd = filters.amd?.trim();
  if (amd && amd.toLowerCase() !== "all") params.set("amd", amd);
  const match = filters.match?.trim().toLowerCase();
  if (match && match !== "all") {
    if (match === "yes" || match === "true" || match === "1") params.set("match", "true");
    else if (match === "no" || match === "false" || match === "0") params.set("match", "false");
  }
  const outcome = filters.outcome?.trim();
  if (outcome && outcome.toLowerCase() !== "all") params.set("outcome", outcome);
  return params.toString();
}

/**
 * Recording filename only. Never returns a directory path.
 */
export function recordingBasename(path: string | null | undefined): string | null {
  if (!path) return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  const name = trimmed.replace(/\\/g, "/").split("/").pop()?.trim() ?? "";
  if (!name || name === "." || name === "..") return null;
  return name;
}

function safeFilename(name: string | null | undefined, fallback: string): string {
  const base = recordingBasename(name) ?? "";
  const cleaned = base.replace(/[^\w.\-]+/g, "_");
  return cleaned || fallback;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  const raw = (star?.[1] ?? plain?.[1] ?? "").trim();
  if (!raw) return null;
  try {
    return recordingBasename(decodeURIComponent(raw.replace(/"/g, "").trim()));
  } catch {
    return recordingBasename(raw);
  }
}

function parseStringList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((item) => String(item));
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item));
    } catch {
      return [trimmed];
    }
  }
  return [];
}

function parseOperatorDecisions(raw: unknown): OperatorDecisionRow[] {
  let value: unknown = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      value = JSON.parse(trimmed) as unknown;
    } catch {
      return [{ action: trimmed }];
    }
  }
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === "string") return { action: item };
    const data = asRecord(item);
    return {
      ts: str(data.ts) ?? str(data.timestamp),
      action: str(data.action),
      digits: str(data.digits),
      attempt: num(data.attempt),
    };
  });
}

function stringifyLogLine(item: unknown): string {
  if (item === null || item === undefined) return "";
  if (typeof item === "string") return item;
  if (typeof item !== "object") return String(item);
  const data = asRecord(item);
  const line = data.line ?? data.message ?? data.text ?? data.msg;
  if (typeof line === "string") {
    const ts = str(data.ts) ?? str(data.timestamp);
    return ts ? `${ts} ${line}` : line;
  }
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
}

/**
 * Accept `{ lines }`, `{ logs }`, `{ text }`, or a bare array / string.
 */
export function parseLogLines(data: unknown): string[] {
  if (data === null || data === undefined) return [];
  if (typeof data === "string") {
    return data.split(/\r?\n/).filter((line) => line.length > 0);
  }
  if (Array.isArray(data)) {
    return data.map(stringifyLogLine).filter((line) => line.length > 0);
  }
  const record = asRecord(data);
  if (typeof record.text === "string") {
    return record.text.split(/\r?\n/).filter((line) => line.length > 0);
  }
  const rows =
    record.lines ??
    record.logs ??
    record.rows ??
    record.items ??
    record.entries ??
    record.recent ??
    record.tail;
  if (Array.isArray(rows)) {
    return rows.map(stringifyLogLine).filter((line) => line.length > 0);
  }
  return [];
}

async function fetchBlob(
  path: string,
  accept: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = {
    Accept: accept,
    ...csrfHeaders(),
  };
  const response = await fetch(`${BASE}${path}`, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    const parsed = await parseBody(response);
    throw new ApiError(
      response.status,
      detailFromBody(parsed, response.statusText || `HTTP ${response.status}`),
      parsed,
    );
  }
  const blob = await response.blob();
  return { blob, filename: filenameFromDisposition(response.headers.get("Content-Disposition")) };
}

/**
 * Trigger a same-tab file download from a blob. Revokes the object URL after click.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safeFilename(filename, "download");
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

async function logsOrEmpty(path: string): Promise<string[]> {
  try {
    const data = await request<unknown>(path);
    return parseLogLines(data);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) return [];
    throw err;
  }
}

/**
 * Normalize a history row from either the web contract or a raw CallRecord dump.
 */
export function normalizeCallRecord(raw: unknown, index = 0): CallRecord {
  const data = asRecord(raw);
  const id = str(data.id) ?? `call-${index}`;
  const timestamp = str(data.timestamp) ?? str(data.ts) ?? str(data.created_at) ?? "";
  const destination = str(data.destination) ?? str(data.dest) ?? str(data.phone) ?? "";
  const attempts = data.digits_attempts;
  const row: CallRecord = {
    id,
    lead_id: num(data.lead_id),
    timestamp,
    destination,
    cli_used: str(data.cli_used) ?? str(data.cli),
    customer_name: str(data.customer_name) ?? str(data.name),
    amd_result: str(data.amd_result) ?? str(data.amd),
    call_type: str(data.call_type),
    digits_attempts: parseStringList(attempts),
    operator_decisions: parseOperatorDecisions(data.operator_decisions),
    code: str(data.code),
    digits: str(data.digits) ?? str(data.code),
    final_match: boolish(data.final_match),
    duration_ms: num(data.duration_ms),
    hangup_cause: str(data.hangup_cause) ?? str(data.cause),
    recording_path: recordingBasename(str(data.recording_path)),
    trunk_id: str(data.trunk_id),
    country_iso2: str(data.country_iso2),
    far_end_class: str(data.far_end_class),
    success_class: str(data.success_class),
    judgment_outcome: str(data.judgment_outcome),
    // Raw stored CDR judgment. Kept verbatim (never historyJudge-rewritten) so
    // the UI can show a neutral `code-match: fail` chip on Accepted successes.
    judgment_raw: str(data.judgment_raw) ?? str(data.judgment_outcome),
    otp_state: str(data.otp_state),
    media_quality_band: str(data.media_quality_band),
    // undefined = API predates evidence field (unknown); null = backend
    // could not find/stat the file (missing evidence).
    recording_bytes:
      data.recording_bytes === undefined ? undefined : num(data.recording_bytes),
  };
  row.outcome = historyOutcome(row);
  const match = historyMatch(row);
  if (match !== null) row.final_match = match;
  const otp = historyOtpState(row);
  if (otp) row.otp_state = otp;
  const entered = historyEnteredCode(row);
  row.code = entered || null;
  row.digits = entered || null;
  return row;
}

/**
 * Accept `{ records }`, `{ calls }`, or a bare array.
 */
export function parseHistory(data: unknown): CallRecord[] {
  if (Array.isArray(data)) return data.map((row, index) => normalizeCallRecord(row, index));
  const record = asRecord(data);
  const rows = record.records ?? record.calls ?? record.items;
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => normalizeCallRecord(row, index));
}

/**
 * Normalize GET /api/session into the console snapshot.
 */
export function normalizeSession(raw: unknown): ConsoleSnapshot {
  const data = asRecord(raw);
  const hero = asRecord(data.hero);
  const code = asRecord(data.code);
  const actions = asRecord(data.actions);
  const lead = asRecord(data.lead);
  const health = asRecord(data.health);
  let sipText = str(data.sip_text) ?? str(asRecord(data.sip).text) ?? undefined;
  const sipCss = str(data.sip_css) ?? str(asRecord(data.sip).css) ?? undefined;
  if (!sipText && health.esl === true) sipText = "SIP UNKNOWN";
  const sip = sipFromLight(sipText, sipCss);
  const state = (str(data.state) as ConsoleSnapshot["state"]) || "IDLE";
  const party = {
    ...EMPTY_PARTY,
    dest: str(hero.dest) ?? str(data.dest) ?? str(lead.phone),
    name: str(hero.name) ?? str(data.customer_name) ?? str(lead.customer_name),
    cli: str(hero.cli) ?? str(data.cli),
    amd: str(hero.amd) ?? str(data.amd),
    attempt: Number(hero.attempt ?? data.attempt ?? 0) || 0,
    max_attempts: Number(hero.max_attempts ?? data.max_attempts ?? 3) || 3,
    park_remaining_s: num(hero.park_remaining_s ?? data.park_remaining_s),
    last_decision: (hero.last_decision ?? data.last_decision ?? null) as ConsoleSnapshot["party"]["last_decision"],
    hangup_cause: str(data.hangup_cause),
    uuid: str(data.uuid) ?? str(hero.uuid),
  };
  const amdLabel =
    str(data.amd_label) ?? str(hero.amd_label) ?? str(hero.amd) ?? party.amd ?? null;
  if (amdLabel) party.amd = amdLabel;
  const detail = str(data.detail) ?? str(hero.detail);
  const ivr = asRecord(data.ivr);
  const collecting = isCodeCollectState(state, detail);
  const gate = isGateState(state, detail);
  const entered = str(code.entered) ?? str(data.digits) ?? "";
  return {
    ...EMPTY_SNAPSHOT,
    state,
    detail,
    heroCopy: str(hero.copy),
    amd_label: amdLabel,
    far_end_class: str(data.far_end_class) ?? str(hero.far_end_class),
    judgment_outcome: str(data.judgment_outcome) ?? str(hero.judgment_outcome),
    trunk_id: str(data.trunk_id) ?? str(hero.trunk_id),
    otp_state: str(data.otp_state) ?? str(hero.otp_state),
    scriptId: str(ivr.id) ?? str(data.pack) ?? str(data.script_id),
    scriptNode: str(ivr.node) ?? str(data.script_node),
    spokenLanguage: str(data.spoken_language) ?? str(hero.spoken_language),
    digitKind: collecting ? "otp" : gate ? "gate" : "",
    party,
    digits: collecting ? entered : "",
    expected_code: str(code.expected) ?? str(data.expected_code) ?? str(lead.expected_code),
    codeMatch: collecting && typeof code.match === "boolean" ? code.match : null,
    actions: {
      ...EMPTY_ACTIONS,
      accept: Boolean(actions.accept),
      retry: Boolean(actions.retry),
      resend: Boolean(actions.resend),
      hangup: Boolean(actions.hangup),
      otp_sent: Boolean(actions.otp_sent),
      new_call: actions.new_call === undefined ? true : Boolean(actions.new_call),
      redial: Boolean(actions.redial),
      next_lead: Boolean(actions.next_lead),
      listen: typeof actions.listen === "boolean" ? actions.listen : undefined,
    },
    listening: typeof data.listening === "boolean" ? data.listening : undefined,
    sip: sip.status,
    sipText: sip.text,
    sipCss: sip.css,
    mode:
      (str(data.mode) as ConsoleSnapshot["mode"]) ||
      (str(health.mode) as ConsoleSnapshot["mode"]) ||
      EMPTY_SNAPSHOT.mode,
  };
}

export const CID_KINDS = ["optimal", "landline", "mobile", "premium"] as const;
export type CidKind = (typeof CID_KINDS)[number];

export const api = {
  health: () => request<HealthResponse>("/api/health"),
  setupStatus: () => request<SetupStatus>("/api/setup"),
  submitSetup: (body: SetupPayload) => request<{ ready: boolean }>("/api/setup", { body }),
  login: (password: string) => request<{ ok: boolean }>("/api/login", { body: { password } }),
  logout: () => request<{ ok: boolean }>("/api/logout", { body: {} }),
  me: () => request<{ authenticated: boolean }>("/api/me"),
  session: () => request<unknown>("/api/session").then(normalizeSession),

  originate: (body: CallSetup) => request<unknown>("/api/session/originate", { body }),
  redial: () => request<unknown>("/api/session/redial", { body: {} }),
  decide: (action: "Accept" | "Retry" | "Resend" | "Hangup") =>
    request<{ ok?: boolean }>("/api/session/decide", { body: { action } }),
  hangup: () => request<{ ok?: boolean }>("/api/session/hangup", { body: {} }),
  otpSent: () => request<{ ok?: boolean }>("/api/session/otp-sent", { body: {} }),

  leads: () => request<{ leads: Lead[] }>("/api/leads"),
  importLeads: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{ leads?: Lead[]; count?: number }>("/api/leads/import", { form });
  },
  leadDialForm: (id: number) => request<CallSetup>(`/api/leads/${id}/dial-form`, { body: {} }),

  history: (limit = 200, filters: HistoryFilters = {}) =>
    request<{ records?: CallRecord[]; calls?: CallRecord[] } | CallRecord[]>(
      `/api/history?${historySearchParams(limit, filters)}`,
    ).then((data) => ({ records: parseHistory(data) })),

  historyCsv: (limit = 200, filters: HistoryFilters = {}) =>
    fetchBlob(`/api/history.csv?${historySearchParams(limit, filters)}`, "text/csv,application/octet-stream,*/*").then(
      ({ blob, filename }) => ({ blob, filename: filename ?? "cdialer-history.csv" }),
    ),

  recording: (id: string) =>
    fetchBlob(`/api/recordings/${encodeURIComponent(id)}`, "audio/wav,application/octet-stream,*/*").then(
      ({ blob, filename }) => ({ blob, filename }),
    ),

  appLogs: () => logsOrEmpty("/api/logs/app"),
  jsonlLogs: () => logsOrEmpty("/api/logs/jsonl"),

  stats: async (): Promise<StatsApiResponse | null> => {
    try {
      return await request<StatsApiResponse>("/api/stats");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) return null;
      throw err;
    }
  },

  adminOverview: async (): Promise<AdminOverview | null> => {
    try {
      const raw = await request<AdminOverview>("/api/admin/overview");
      if (!raw) return raw;
      return { ...raw, sip: sipStatusLabel(raw.sip, "") };
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) return null;
      throw err;
    }
  },

  packs: () =>
    request<{ packs: string[] | { slug: string; label: string }[] }>("/api/scripts").then((data) => ({
      packs: (data.packs ?? []).map((item) =>
        typeof item === "string" ? { slug: item, label: item } : item,
      ),
    })),
  scripts: (pack = "default") =>
    request<{ pack?: string; text?: string; body?: string; slug?: string; stages?: Record<string, string> }>(
      `/api/scripts/${encodeURIComponent(pack)}`,
    ).then((data) => ({
      slug: data.pack ?? data.slug ?? pack,
      body: data.text ?? data.body ?? "",
      stages: data.stages ?? {},
    })),
  saveScript: (pack: string, text: string) =>
    request<{ ok?: boolean; pack?: string; text?: string; errors?: unknown[] }>(
      `/api/scripts/${encodeURIComponent(pack)}`,
      {
        method: "PUT",
        body: { text },
      },
    ),
  scriptCatalog: () => request<Record<string, unknown>>("/api/scripts/catalog"),
  languages: () =>
    request<{ languages?: Array<{ code?: string; label?: string }> }>("/api/languages").then((data) =>
      (data.languages ?? [])
        .map((item) => ({
          code: String(item.code ?? "").trim(),
          label: String(item.label ?? item.code ?? "").trim(),
        }))
        .filter((item) => item.code),
    ),
  dialCatalog: () =>
    request<{ catalog?: Array<Record<string, unknown>> }>("/api/orchestrator/catalog").then((data) =>
      (data.catalog ?? [])
        .map((row) => ({
          iso2: String(row.iso2 ?? "").trim().toUpperCase(),
          name: String(row.name ?? row.iso2 ?? "").trim(),
          calling_code: String(row.calling_code ?? "").replace(/\D/g, ""),
        }))
        .filter((row) => row.iso2 && row.calling_code),
    ),
  validateScript: (body: { kind?: "script" | "ivr"; text?: string; graph?: unknown }) =>
    request<{ ok: boolean; errors: Array<{ path: string; pos: number; line: number; col: number; message: string }> }>(
      "/api/scripts/validate",
      { method: "POST", body },
    ),

  sip: () =>
    request<{
      profiles: SipProfile[];
      active_id?: string;
      active?: string;
      public_ip?: string;
      dtmf?: Record<string, unknown>;
    }>("/api/sip").then((data) => ({
      profiles: data.profiles ?? [],
      active: data.active_id ?? data.active ?? "",
      public_ip: data.public_ip ?? "",
      dtmf: data.dtmf,
    })),
  createSip: (body: SipProfileWrite) => request<SipProfile>("/api/sip", { body }),
  updateSip: (id: string, body: SipProfileWrite) =>
    request<SipProfile>(`/api/sip/${encodeURIComponent(id)}`, { method: "PUT", body }),
  deleteSip: (id: string) =>
    request<{ ok: boolean }>(`/api/sip/${encodeURIComponent(id)}`, { method: "DELETE" }),
  applySip: (id: string) =>
    request<{
      ok: boolean;
      state?: string;
      sofia_state?: string;
      message?: string;
      need_sudo?: boolean;
      register?: boolean;
    }>(`/api/sip/${encodeURIComponent(id)}/apply`, { body: {} }),
  testSip: (id: string) =>
    request<{ state?: string; sofia_state?: string }>(
      `/api/sip/${encodeURIComponent(id)}/test`,
      { body: {} },
    ),
  activateSip: (id: string) =>
    request<{ ok: boolean }>(`/api/sip/${encodeURIComponent(id)}/active`, { body: {} }),
  dtmfRange: (id: string) =>
    request<DtmfRangeResult>(`/api/sip/${encodeURIComponent(id)}/dtmf-range`, { body: {} }),

  settings: () => request<SettingsPayload>("/api/settings"),
  saveSettings: (body: SettingsPayload) =>
    request<{ ok?: boolean }>("/api/settings", { method: "PATCH", body }),

  /**
   * Rotate one or more secrets. Backend returns `{ok, flags, rotated}`;
   * this unwraps the nested `has_*` flags (never secret values). Empty
   * / undefined fields are ignored server-side.
   */
  saveSecrets: (body: SecretPatch) =>
    request<{ ok?: boolean; flags?: Partial<SecretFlags> } & Partial<SecretFlags>>(
      "/api/settings/secrets",
      { method: "POST", body },
    ).then((data) => {
      const nested = data.flags && typeof data.flags === "object" ? data.flags : data;
      return {
        has_esl_password: Boolean(nested.has_esl_password),
        has_fish_key: Boolean(nested.has_fish_key),
        has_bitcall: Boolean(nested.has_bitcall),
        has_cd: Boolean(nested.has_cd),
        has_deepl: Boolean(nested.has_deepl),
        has_admin: Boolean(nested.has_admin),
      };
    }),

  profileNames: () =>
    request<{ names: string[] } | CallProfile[] | { profiles: CallProfile[] }>("/api/profiles").then((data) => {
      if (Array.isArray(data)) return data.map((item) => item.name);
      if ("names" in data && Array.isArray(data.names)) return data.names;
      if ("profiles" in data && Array.isArray(data.profiles)) return data.profiles.map((item) => item.name);
      return [];
    }),
  profile: (name: string) => request<CallProfile>(`/api/profiles/${encodeURIComponent(name)}`),
  saveProfile: (body: CallProfile) => request<CallProfile>("/api/profiles", { method: "PUT", body }),
  deleteProfile: (name: string) =>
    request<{ ok: boolean }>(`/api/profiles/${encodeURIComponent(name)}`, { method: "DELETE" }),

  cliKinds: (sip: string) =>
    request<{ kinds: Record<string, string[]>; optimal: Record<string, string> }>(
      `/api/cli/kinds?sip=${encodeURIComponent(sip.trim())}`,
    ),
  randomCid: (country: string, kind: CidKind = "optimal", sip = "") => {
    const iso = country.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) {
      return Promise.reject(new Error("CID country is required"));
    }
    const picked = (CID_KINDS as readonly string[]).includes(kind) ? kind : "optimal";
    return request<{ cli: string }>("/api/cli/random", {
      body: { country: iso, kind: picked, sip: sip.trim() },
    });
  },
  nextCid: () => request<{ cli: string }>("/api/cli/next", { body: {} }),

  listIvr: () =>
    request<{ scripts?: IvrScript[]; items?: IvrScript[] } | IvrScript[]>("/api/ivr").then((data) => ({
      scripts: Array.isArray(data) ? data : (data.scripts ?? data.items ?? []),
    })),
  getIvr: (slug: string) => request<IvrScript>(`/api/ivr/${encodeURIComponent(slug)}`),
  saveIvr: (slug: string, graph: IvrGraph, name?: string) =>
    request<IvrScript>(`/api/ivr/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: name ? { graph, name } : { graph },
    }),
  activateIvr: (slug: string, version: number) =>
    request<IvrScript>(`/api/ivr/${encodeURIComponent(slug)}/activate`, {
      body: { version },
    }),

  listCampaigns: () =>
    request<{ campaigns?: Campaign[]; items?: Campaign[] } | Campaign[]>("/api/campaigns").then((data) => ({
      campaigns: Array.isArray(data) ? data : (data.campaigns ?? data.items ?? []),
    })),
  saveCampaign: (body: Campaign) =>
    body.id
      ? request<Campaign>(`/api/campaigns/${encodeURIComponent(body.id)}`, { method: "PUT", body })
      : request<Campaign>("/api/campaigns", { body }),
  campaignReports: (id: string) =>
    request<CampaignReport>(`/api/campaigns/${encodeURIComponent(id)}/reports`),

  adaptersStatus: async (): Promise<AdaptersStatus | null> => {
    try {
      return await request<AdaptersStatus>("/api/adapters");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) return null;
      throw err;
    }
  },

  sessionListen: () => request<{ ok?: boolean; listening?: boolean }>("/api/session/listen", { body: {} }),
  sessionListenStop: () => request<{ ok?: boolean }>("/api/session/listen-stop", { body: {} }),
};
